import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import "./Dashboard.css";

/**
 * RenameChatDialog - Dialog to rename a chat, positioned near the specific chat in the sidebar
 * @param {boolean} open - Whether dialog is open
 * @param {Object} anchorRect - Optional { top, left, bottom, right } to position dialog near the chat row
 * @param {string} chatId - ID of the chat being renamed
 * @param {string} currentTitle - Current title to show in input
 * @param {Function} onSave - (chatId, newTitle) => void
 * @param {Function} onClose - () => void
 * @param {boolean} darkMode - Current dark mode state
 * @param {string} label - Optional label e.g. "Chat" or "Debate"
 */
export default function RenameChatDialog({
  open,
  anchorRect,
  chatId,
  currentTitle = "",
  onSave,
  onClose,
  darkMode = false,
  label = "Chat",
}) {
  const [title, setTitle] = useState(currentTitle);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitle(currentTitle);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, currentTitle]);

  if (!open || typeof document === "undefined" || !document.body) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;
    onSave(chatId, trimmed);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const dialogWidth = 760;
  const dialogContent = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000000,
        pointerEvents: "none",
      }}
    >
      <motion.div
        role="dialog"
        aria-label={`Rename ${label}`}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.15 }}
        className="rename-chat-dialog"
        style={{
          width: `${dialogWidth}px`,
          maxWidth: "90vw",
          maxHeight: "90vh",
          pointerEvents: "auto",
          backgroundColor: darkMode ? "#1e293b" : "#ffffff",
        border: `1px solid ${darkMode ? "#475569" : "#e5e7eb"}`,
        borderRadius: "0.5rem",
        boxShadow: darkMode
          ? "0 10px 25px -5px rgba(0, 0, 0, 0.5)"
          : "0 10px 25px -5px rgba(0, 0, 0, 0.15)",
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.25rem",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: darkMode ? "#f1f5f9" : "#1e293b",
          }}
        >
          Rename {label}
        </h3>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0.25rem",
            color: darkMode ? "#94a3b8" : "#64748b",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`${label} name`}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "0.5rem 0.75rem",
            fontSize: "0.875rem",
            border: `1px solid ${darkMode ? "#475569" : "#e2e8f0"}`,
            borderRadius: "0.375rem",
            backgroundColor: darkMode ? "#0f172a" : "#f8fafc",
            color: darkMode ? "#f1f5f9" : "#1e293b",
            outline: "none",
            marginBottom: "0.75rem",
          }}
        />
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              border: `1px solid ${darkMode ? "#475569" : "#e2e8f0"}`,
              borderRadius: "0.375rem",
              background: "transparent",
              color: darkMode ? "#cbd5e1" : "#475569",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!title.trim()}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              border: "none",
              borderRadius: "0.375rem",
              background: title.trim() ? "#2563eb" : (darkMode ? "#334155" : "#cbd5e1"),
              color: "#fff",
              cursor: title.trim() ? "pointer" : "not-allowed",
            }}
          >
            Save
          </button>
        </div>
      </form>
    </motion.div>
    </div>
  );

  return createPortal(
    <>
      <div
        role="presentation"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999999,
          backgroundColor: "rgba(0,0,0,0.2)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
        onClick={onClose}
        onMouseDown={onClose}
      />
      {dialogContent}
    </>,
    document.body
  );
}
